/**
 * BoundingBoxUtils.ts - Tiện ích chuẩn hoá và trích xuất Bounding Box không gian.
 * Hỗ trợ nhận diện và ưu tiên iqr_bbox (chống outlier điểm CAD/DGN).
 */

/**
 * Helper parse bất kỳ định dạng bounding box nào thành [minX, minY, maxX, maxY]
 */
export const parseBoundingBox = (val: any): [number, number, number, number] | null => {
  if (!val) return null;

  if (typeof val === 'string') {
    const trimmed = val.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      try {
        const parsed = JSON.parse(trimmed);
        if (Array.isArray(parsed) && parsed.length >= 4) {
          return parseBoundingBox(parsed);
        }
      } catch {
        // fallback to comma parsing
      }
    }
    if (trimmed.includes(',')) {
      const cleaned = trimmed.replace(/[\[\]]/g, '');
      const nums = cleaned.split(',').map((s) => parseFloat(s.trim()));
      if (nums.length >= 4 && !nums.some(isNaN)) {
        return [nums[0], nums[1], nums[2], nums[3]];
      }
    }
  }

  if (Array.isArray(val) && val.length >= 4) {
    const nums = val.slice(0, 4).map(Number);
    if (!nums.some(isNaN)) {
      return [nums[0], nums[1], nums[2], nums[3]];
    }
  }

  return null;
};

/**
 * Lựa chọn bounding box tối ưu nhất theo thứ tự ưu tiên:
 * 1. iqr_bbox (loại bỏ nhiễu/outlier của CAD/DGN)
 * 2. bbox (chuẩn OGC / WGS84)
 * 3. bounds / boundingBox (fallback)
 */
export const getPreferredBoundingBox = (
  metadata?: any,
  fallbackDirectBounds?: any,
): [number, number, number, number] | null => {
  if (metadata) {
    const metaObj = typeof metadata === 'string' ? (() => {
      try { return JSON.parse(metadata); } catch { return {}; }
    })() : metadata;

    const iqr = parseBoundingBox(metaObj.iqr_bbox);
    if (iqr) return iqr;

    const b = parseBoundingBox(metaObj.bbox);
    if (b) return b;

    const directMeta = parseBoundingBox(metaObj.bounds || metaObj.boundingBox);
    if (directMeta) return directMeta;
  }

  if (fallbackDirectBounds) {
    const direct = parseBoundingBox(fallbackDirectBounds);
    if (direct) return direct;
  }

  return null;
};
